import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { finalize, Observable, Subscription } from 'rxjs';
import dayjs from 'dayjs';
import { MyOskJoinInvitationsService } from '../../../../api/api-services/my-osk-join-invitations.service';
import { MyOskJoinInvitationService } from '../../../../api/api-services/my-osk-join-invitation.service';
import { MyOskJoinInvitationResendService } from '../../../../api/api-services/my-osk-join-invitation-resend.service';
import { IMyOskJoinInvitationVm } from '../../../../api/my-osk-join-invitation-vm.model';
import {
  OSK_JOIN_INVITATION_STATUS_LABELS,
  OskJoinInvitationStatus,
} from '../../../../api/osk-join-invitation-status.model';

/**
 * The "Zaproszeni" tab: invitations to students who already had an account. They join the OSK only by accepting -
 * until then the OSK sees just the e-mail it typed. A pending invitation can be sent again or withdrawn.
 */
@Component({
  selector: 'app-students-invitations',
  standalone: true,
  imports: [CommonModule, ButtonModule, ConfirmDialogModule, TableModule, TooltipModule],
  providers: [ConfirmationService],
  templateUrl: './students-invitations.component.html',
  styleUrl: './students-invitations.component.scss',
})
export class StudentsInvitationsComponent implements OnInit, OnDestroy {
  /** Emits when a student was added or invited from the page, so a new invitation shows up. */
  @Input() refresh?: Observable<void>;

  private readonly invitationsService = inject(MyOskJoinInvitationsService);
  private readonly invitationService = inject(MyOskJoinInvitationService);
  private readonly resendService = inject(MyOskJoinInvitationResendService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly messageService = inject(MessageService);

  public invitations: IMyOskJoinInvitationVm[] = [];
  public loading = false;
  public loadError = false;
  /** The invitation an action is running for. */
  public busyGuid: string | null = null;

  private refreshSubscription?: Subscription;

  ngOnInit(): void {
    this.load();
    this.refreshSubscription = this.refresh?.subscribe(() => this.load());
  }

  ngOnDestroy(): void {
    this.refreshSubscription?.unsubscribe();
  }

  load(): void {
    this.loading = true;
    this.loadError = false;
    this.invitationsService
      .get()
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (invitations) => (this.invitations = invitations ?? []),
        error: () => (this.loadError = true),
      });
  }

  resend(invitation: IMyOskJoinInvitationVm): void {
    this.busyGuid = invitation.guid;
    this.resendService
      .post({ invitationGuid: invitation.guid })
      .pipe(finalize(() => (this.busyGuid = null)))
      .subscribe({
        next: (updated) => {
          this.invitations = this.invitations.map((item) =>
            item.guid === invitation.guid ? { ...item, expiresUtc: updated.expiresUtc } : item
          );
          this.messageService.add({
            severity: 'success',
            summary: 'Sukces',
            detail: `Zaproszenie wysłane ponownie na ${invitation.email}. Kursant ma 14 dni na odpowiedź.`,
          });
        },
        error: (error: HttpErrorResponse) => {
          this.showError(error, 'Nie udało się wysłać zaproszenia ponownie.');
          this.load();
        },
      });
  }

  cancel(invitation: IMyOskJoinInvitationVm): void {
    this.confirmationService.confirm({
      key: 'studentInvitationConfirm',
      header: 'Wycofanie zaproszenia',
      message: `Wycofać zaproszenie dla ${invitation.email}? Kursant nie będzie mógł go już przyjąć, a zarezerwowany abonament wróci do puli.`,
      acceptLabel: 'Wycofaj',
      rejectLabel: 'Anuluj',
      defaultFocus: 'reject',
      accept: () => {
        this.busyGuid = invitation.guid;
        this.invitationService
          .delete(invitation.guid)
          .pipe(finalize(() => (this.busyGuid = null)))
          .subscribe({
            next: () => {
              this.invitations = this.invitations.filter((item) => item.guid !== invitation.guid);
              this.messageService.add({ severity: 'success', summary: 'Sukces', detail: 'Zaproszenie zostało wycofane.' });
            },
            error: (error: HttpErrorResponse) => {
              this.showError(error, 'Nie udało się wycofać zaproszenia.');
              this.load();
            },
          });
      },
    });
  }

  statusLabel(invitation: IMyOskJoinInvitationVm): string {
    return OSK_JOIN_INVITATION_STATUS_LABELS[invitation.status] ?? '';
  }

  isPending(invitation: IMyOskJoinInvitationVm): boolean {
    return invitation.status === OskJoinInvitationStatus.Pending;
  }

  formatDate(date?: Date): string {
    return date ? dayjs(date).format('DD.MM.YYYY HH:mm') : '—';
  }

  private showError(error: HttpErrorResponse, fallback: string): void {
    const apiMessage = typeof error?.error === 'string' ? error.error : error?.error?.message;
    this.messageService.add({ severity: 'error', summary: 'Błąd', detail: apiMessage || fallback });
  }
}
